import React, { useState, useEffect } from 'react';
import { TextField, Button, Typography, Card, CardContent, InputAdornment, Alert, CircularProgress } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { authServices } from '../../services/authentication/authServices';
import '../../css/Password.css';

const ResetPassword = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [passwordError, setPasswordError] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;

    // Redirect if no email in state (direct URL access or OTP not verified)
    useEffect(() => {
        if (!email) {
            navigate('/forgot-password');
        }
    }, [email, navigate]);

    const validatePasswordRules = (password) => {
        const hasCapital = /[A-Z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (!hasCapital) return 'Password must contain at least one capital letter.';
        if (!hasNumber) return 'Password must contain at least one number.';
        if (!hasSpecial) return 'Password must contain at least one special character.';
        return '';
    };

    const handleNewPasswordChange = (e) => {
        const value = e.target.value;
        setNewPassword(value);
        setPasswordError(validatePasswordRules(value));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setError('New password and confirm password do not match.');
            setTimeout(() => setError(null), 4000);
            return;
        }

        const validationError = validatePasswordRules(newPassword);
        if (validationError) {
            setError(validationError);
            setTimeout(() => setError(null), 4000);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            await authServices.resetPassword(email, newPassword, confirmPassword);
            // Navigate immediately on success; pass message so Login can show a snackbar
            navigate('/login', { state: { successMessage: 'Password reset successfully! Please login with your new password.' } });
        } catch (err) {
            const msg = err.response?.data || 'Failed to reset password. Please try again.';
            setError(msg);
            setTimeout(() => setError(null), 5000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-page-wrapper">
            <Card sx={{ width: 400, padding: '1.25rem', borderRadius: '1rem', boxShadow: 3, backgroundColor: '#ffffff' }}>
                <CardContent>
                    <Typography variant="h3" align="center" gutterBottom sx={{
                        fontWeight: 700, fontSize: '2rem', color: '#045864', letterSpacing: '2px'
                    }}>
                        Reset Password
                    </Typography>

                    {error && (
                        <Alert variant="outlined" severity="error" sx={{ marginTop: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <TextField
                            label="New Password"
                            variant="outlined"
                            fullWidth
                            type="password"
                            value={newPassword}
                            onChange={handleNewPasswordChange}
                            required
                            margin="normal"
                            error={!!passwordError}
                            helperText={passwordError}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <i className="fa fa-lock auth-input-icon"></i>
                                        </InputAdornment>
                                    ),
                                }
                            }}
                        />

                        <TextField
                            label="Confirm New Password"
                            variant="outlined"
                            fullWidth
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            margin="normal"
                            error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                            helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? 'Passwords do not match.' : ''}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <i className="fa fa-lock auth-input-icon"></i>
                                        </InputAdornment>
                                    ),
                                }
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                marginTop: 2,
                                padding: '0.75rem',
                                fontSize: '1rem',
                                textTransform: 'none',
                                backgroundColor: '#045864',
                                '&:hover': { backgroundColor: '#034a54' }
                            }}
                        >
                            {loading ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
                        </Button>

                        <div className="auth-back-link">
                            <Button
                                variant="text"
                                onClick={() => navigate('/login')}
                                sx={{ color: '#045864', textTransform: 'none', fontSize: '0.875rem' }}
                            >
                                ← Back to Login
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default ResetPassword;
